import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { combineLatest, filter, Observable, of, Subscription, take } from 'rxjs';
import { StateManagementService } from '../../shared/services/state-management.service';
import { HomeService } from '../knowledge-base/_services/home.service';
import { SessionStorageService } from 'angular-web-storage';
import { roleAccess } from '../../shared/services/state-management.service';

interface Auth0Error {
  error?: string;
  error_description?: string;
  message?: string;
}
@Component({
  selector: 'app-login',
  imports: [],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class Login {

  private stateManagementService = inject(StateManagementService);
  isAuthenticated$: Observable<boolean> = new Observable<boolean>();
  isAuthError: boolean = false;
  currentRoute: string | undefined = undefined;
  eventListeners: Subscription[] = [];
  loginMessage = '';

  constructor(
    private auth: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private homeService: HomeService,
    public session: SessionStorageService
  ) { }
  i18n: any = '';
  ngOnInit(): void {
    this.currentRoute = this.activatedRoute.snapshot.routeConfig?.path;
    this.setLoginMessage();
    this.listenToAuthChanges();
    this.homeService.loadi18().subscribe((value: any) => {
      this.i18n = value.return_body;
      this.session.set("i18n", this.i18n);
    });
  }

  private setLoginMessage(): void {
    this.activatedRoute.queryParamMap.subscribe(params => {
      this.loginMessage =
        params.get('reason') === 'session-timeout'
          ? 'Your session has expired due to inactivity. Please sign in again to continue.'
          : 'Please log in to access your account.';
    });
  }

  listenToAuthChanges(): void {
    this.isAuthenticated$ = this.auth.isAuthenticated$;

    const authErrorSubscription = this.auth.error$.subscribe((err: Auth0Error) => {
      if (err) {
        this.isAuthError = true;
        const error_message = err.error + ' - ' + (err.error_description || err.message || 'An unknown error occurred');
        alert('Authentication error: ' + error_message);
        sessionStorage.clear();
        this.stateManagementService.removeLocalStorage('isValidUser');
        this.stateManagementService.removeLocalStorage('jwtToken');
        this.stateManagementService.removeLocalStorage('userValue');
        this.stateManagementService.removeLocalStorage('platformToken');
        this.stateManagementService.removeLocalStorage('userRoleAccessToken');
        this.stateManagementService.removeLocalStorage('userValue');
        this.router.navigate(['/user/invalid']);
      }
    });
    this.eventListeners.push(authErrorSubscription);


    const authStatusSubscription = combineLatest([
      this.auth.isLoading$,
      this.isAuthenticated$,
    ])
      .pipe(filter(([isLoading]) => !isLoading))
      .subscribe(([, isAuth]) => {
        console.log('Authentication status changed:', isAuth);
        this.stateManagementService.setLoggedInStatus(isAuth);
        if (isAuth) {
          this.isAuthError = false;
          if (this.currentRoute === 'user/validate') {
            console.log('Login successful route accessed. Validating user...');
            setTimeout(() => {
              this.auth.idTokenClaims$.pipe(take(1)).subscribe((claims) => {
                const idToken = claims?.__raw;

                if (!idToken) {
                  this.stateManagementService.setIsValidUser(false);
                  this.router.navigate(['/user/invalid']);
                  return;
                }
                this.stateManagementService.validateUser(idToken).subscribe(async (isUserValid) => {
                  this.stateManagementService.setIsValidUser(isUserValid);
                  console.log('User validation result:', isUserValid);
                  console.log('Inside IF:', isUserValid);
                  const channel = new BroadcastChannel('auth');
                  channel.postMessage('login');
                  console.log('After BroadcastChannel', JSON.stringify(localStorage.getItem('userRoleAccessToken')));
                  const decoded = JSON.parse(localStorage.getItem('userRoleAccessToken') || '') as roleAccess | null;
                  if (decoded?.rolePermissions?.reports.view == true) {
                    this.router.navigate(['dashboard']);
                  } else if (decoded?.rolePermissions?.knowledgeBase.view == true || decoded?.rolePermissions?.knowledgeBase.manage == true) {
                    this.router.navigate(['kb']);
                  }
                });
              });
            });
          }
        }
        else if (!this.isAuthError) {
          this.router.navigate(['/login'], {
            queryParamsHandling: 'preserve',
          });
        }
      });

    this.eventListeners.push(authStatusSubscription);
  }

  ngOnDestroy(): void {
    this.eventListeners.forEach(subscription => subscription.unsubscribe());
  }
}
